import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Table, Button, Alert, Badge, Spinner } from 'react-bootstrap';
import { format } from 'date-fns';
import { useParams, useNavigate } from 'react-router-dom';

import PageWrapper from 'components/PageWrapper';
import CommonLoader from 'components/CommonLoader';

import QAApi from 'services/QAApi';
import { MembershipDocumentDto } from 'services/QAApi/types';

import useIsMountedRef from 'utils/hooks/useIsMountedRef';
import useToaster from 'utils/hooks/useToaster';
import HttpErrorHelper from 'utils/HttpErrorHelper';

const FilinnlesningerDetaljer = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMountedRef = useIsMountedRef();
  const toaster = useToaster();
  const toasterRef = useRef(toaster);

  useEffect(() => {
    toasterRef.current = toaster;
  }, [toaster]);

  const [document, setDocument] = useState<MembershipDocumentDto | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [isLoadingDownloadUrl, setIsLoadingDownloadUrl] = useState<boolean>(false);

  useEffect(() => {
    console.log('FilinnlesningerDetaljer: useEffect triggered with id:', id);
    const loadDocument = async () => {
      if (!id) {
        console.log('FilinnlesningerDetaljer: No id provided');
        return;
      }

      console.log('FilinnlesningerDetaljer: Loading document with id:', id);
      setIsLoading(true);
      try {
        const doc = await QAApi.getMembershipDocumentById(id);
        console.log('FilinnlesningerDetaljer: Document loaded:', doc);
        if (isMountedRef.current) {
          setDocument(doc);
        }
      } catch (error) {
        console.error('FilinnlesningerDetaljer: Error loading document:', error);
        if (isMountedRef.current) {
          toaster.error(
            'Feil ved lasting av medlemskapsdokument',
            HttpErrorHelper.formatError(error),
          );
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    loadDocument();
  }, [id]);

  useEffect(() => {
    const loadDownloadUrl = async () => {
      setDownloadUrl(null);

      if (!document?.uploadedFileUrl) {
        return;
      }

      setIsLoadingDownloadUrl(true);
      try {
        const response = await QAApi.getMembershipDocumentFileUrl(document.chainId);
        if (isMountedRef.current) {
          setDownloadUrl(response?.fileUrl || null);
        }
      } catch (error) {
        if (isMountedRef.current) {
          setDownloadUrl(null);
          toasterRef.current.error(
            'Feil ved henting av nedlastingslenke',
            HttpErrorHelper.formatError(error),
          );
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoadingDownloadUrl(false);
        }
      }
    };

    void loadDownloadUrl();
  }, [document?.chainId, document?.uploadedFileUrl, isMountedRef]);

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return '';
    try {
      return format(new Date(date), 'dd.MM.yyyy');
    } catch {
      return 'Ugyldig dato';
    }
  };

  if (isLoading) {
    return (
      <PageWrapper isFluid header="Filinnlesninger - Detaljer">
        <Container fluid>
          <CommonLoader />
        </Container>
      </PageWrapper>
    );
  }

  if (!document) {
    return (
      <PageWrapper isFluid header="Filinnlesninger - Detaljer">
        <Container fluid>
          <Alert variant="warning">Medlemskapsdokumentet ble ikke funnet.</Alert>
          <Button variant="secondary" onClick={() => navigate('/qa/filinnlesninger')}>
            Tilbake til oversikt
          </Button>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper isFluid header={`Filinnlesninger - Detaljer`}>
      <Container fluid>
        <Row className="mb-3">
          <Col>
            <Button variant="secondary" onClick={() => navigate('/qa/filinnlesninger')}>
              ← Tilbake til oversikt
            </Button>
          </Col>
          <Col className="text-end">
            {downloadUrl && (
              <Button
                variant="primary"
                href={downloadUrl}
                target="_blank"
                rel="noreferrer"
                disabled={isLoadingDownloadUrl}
              >
                {isLoadingDownloadUrl ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Henter lenke...
                  </>
                ) : (
                  'Last ned fil'
                )}
              </Button>
            )}
          </Col>
        </Row>

        {/* Document Info */}
        <Card className="mb-3">
          <Card.Header>
            <h5>Dokumentinformasjon</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <dl>
                  <dt>Eier navn:</dt>
                  <dd>{document.ownerName}</dd>

                  <dt>Lesedato:</dt>
                  <dd>{formatDate(document.readAt)}</dd>

                  <dt>Totalt antall medlemmer:</dt>
                  <dd>{document.members.length}</dd>
                </dl>
              </Col>
              <Col md={6}>
                <dl>
                  <dt>Sendt til QA:</dt>
                  <dd>
                    {document.sentToQaAt ? (
                      <Badge bg="success">Ja - {formatDate(document.sentToQaAt)}</Badge>
                    ) : (
                      <Badge bg="secondary">Nei</Badge>
                    )}
                  </dd>

                  {document.sentToQaBy && (
                    <>
                      <dt>Sendt av:</dt>
                      <dd>{document.sentToQaBy}</dd>
                    </>
                  )}
                  <dt>Antall sendt til QA</dt>
                  <dd>{document.endringsMeldingIdsSent.length}</dd>
                </dl>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Members */}
        {document.members.length > 0 ? (
          <Table responsive hover>
            <thead>
              <tr>
                <th>Enhet navn</th>
                <th>Organisasjonsnr</th>
                <th>Sendt til QA?</th>
                <th>Fra dato</th>
                <th>Til dato</th>
              </tr>
            </thead>
            <tbody>
              {document.members.map((member, index) => {
                const uniqueKey = member.uniqueKey ?? '';
                const isUniqueKeySent = document.uniqueKeysSent.includes(uniqueKey);
                return (
                  <tr key={index}>
                    <td>{member.enhetNavn}</td>
                    <td>{member.organisasjonsnummer}</td>
                    <td>{isUniqueKeySent ? 'Ja' : 'Nei'}</td>
                    <td>{formatDate(member.medlemFraOgMedDato)}</td>
                    <td>{formatDate(member.medlemTilOgMedDato)}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        ) : (
          <Alert variant="info" className="m-3">
            Ingen medlemmer funnet.
          </Alert>
        )}
      </Container>
    </PageWrapper>
  );
};

export default FilinnlesningerDetaljer;
